import { useEffect, useRef, useState } from 'react';
import { api, ApiError, type OrderRequest, type RequestStatus, type TableInfo } from '../api';
import { formatMoney } from '../cart';
import type { T } from '../i18n';
import { PayOnline } from './PayOnline';

const FLOW: RequestStatus[] = ['pending_acceptance', 'accepted', 'preparing', 'ready', 'served'];
const TERMINAL: RequestStatus[] = ['rejected', 'cancelled', 'expired', 'completed'];

function position(status: RequestStatus): number {
  if (status === 'queued' || status === 'awaiting_pos') return 0;
  if (status === 'completed') return FLOW.length;
  return FLOW.indexOf(status);
}

type Props = { token: string; requestId: string; info: TableInfo; t: T; onBack: () => void };

const KITCHEN_STARTED: RequestStatus[] = ['preparing', 'ready', 'served'];

/** 1-5 stars and an optional comment, once per served order. */
function RateOrder({ token, request, t, onRated }: { token: string; request: OrderRequest; t: T; onRated: (r: OrderRequest) => void }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      onRated((await api.rate(token, request.id, rating, comment.trim())).request);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rate" data-testid="rate-order">
      <strong>{t('rateTitle')}</strong>
      <div className="stars" role="radiogroup" aria-label={t('rateTitle')}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={t('rateStars', { n })} className={n <= rating ? 'star on' : 'star'} onClick={() => setRating(n)}>
            ★
          </button>
        ))}
      </div>
      <textarea maxLength={500} rows={2} placeholder={t('rateComment')} aria-label={t('rateComment')} value={comment} onChange={(e) => setComment(e.target.value)} />
      {error && <p className="error" role="alert">{error}</p>}
      <button className="btn" disabled={!rating || busy} onClick={() => void send()}>{t('rateSend')}</button>
    </div>
  );
}

export function StatusView({ token, requestId, info, t, onBack }: Props) {
  const [req, setReq] = useState<OrderRequest | null>(null);
  const [online, setOnline] = useState(info.online);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const announce = useRef<string>('');

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const res = await api.status(token, requestId);
        if (stop) return;
        setReq(res.request);
        setOnline(res.online);
        setError(null);
        if (TERMINAL.includes(res.request.status) || res.request.status === 'served') return;
      } catch (e) {
        if (!stop) setError(e instanceof ApiError ? e.message : String(e));
      }
      if (!stop) timer = setTimeout(load, document.hidden ? 10_000 : 3000);
    };
    void load();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [token, requestId]);

  if (!req) return <div className="center">{error ?? '…'}</div>;

  const currency = req.currency ?? info.restaurant.currency;
  const label = t(`status_${req.status}` as Parameters<T>[0]);
  const bad = ['rejected', 'cancelled', 'expired'].includes(req.status);
  const canCancel = ['queued', 'awaiting_pos', 'pending_acceptance'].includes(req.status);
  const canPay = info.ordering.payment_mode !== 'pay_at_counter' && req.accepted && req.payment_status !== 'paid' && req.confirmed_totals;
  if (announce.current !== label) announce.current = label;

  const cancel = async () => {
    if (!window.confirm(t('cancelConfirm'))) return;
    setBusy(true);
    try {
      setReq((await api.cancel(token, req.id)).request);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="status">
      <div className="card">
        <div aria-live="polite">
          <span className={`badge ${bad ? 'bad' : req.accepted ? 'ok' : ''}`}>{label}</span>
        </div>
        {req.order_number && <h2 style={{ margin: '12px 0 0' }}>{t('orderNumber', { n: req.order_number })}</h2>}
        {!req.accepted && !bad && <p className="note">{t('notAcceptedYet')}</p>}
        {!online && !req.accepted && !bad && <p className="banner">{t('restaurantOffline')}</p>}
        {req.status_reason && bad && <p className="error">{req.status_reason}</p>}

        {!bad && (
          <ol className="timeline">
            {FLOW.map((s, i) => {
              const at = position(req.status);
              return (
                <li key={s} className={i < at || req.status === 'completed' ? 'done' : i === at ? 'current' : ''}>
                  <span className="dot" aria-hidden="true" /> {t(`status_${s}` as Parameters<T>[0])}
                </li>
              );
            })}
          </ol>
        )}

        <div className="totals">
          {req.confirmed_totals ? (
            <>
              <div className="grand"><span>{t('tableBill')}</span><span>{formatMoney(req.confirmed_totals.total, currency)}</span></div>
              <p className="note">{t('confirmedByRestaurant')}</p>
            </>
          ) : (
            <>
              <div><span>{t('subtotal')}</span><span>{formatMoney(req.estimated_subtotal, currency)}</span></div>
              <p className="note">{t('estimated')}</p>
            </>
          )}
          {req.payment_status === 'paid' ? <p className="badge ok">{t('paid')}</p> : <p className="note">{info.ordering.payment_mode === 'online_required' ? '' : t('payAtCounter')}</p>}
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="stack">
          {canPay && <PayOnline token={token} request={req} currency={currency} t={t} onPaid={() => setReq({ ...req, payment_status: 'pending' })} />}
          {canCancel && <button className="btn danger" disabled={busy} onClick={cancel}>{t('cancelOrder')}</button>}
          {req.accepted && !canCancel && (
            <p className="note" data-testid="no-cancel">{KITCHEN_STARTED.includes(req.status) ? t('kitchenStarted') : t('acceptedNoCancel')}</p>
          )}
          {req.can_rate && <RateOrder token={token} request={req} t={t} onRated={setReq} />}
          {req.rated && <p className="badge ok">{t('rateThanks')}</p>}
          <button className="btn secondary" onClick={onBack}>{info.ordering.allow_repeat_orders && req.accepted ? t('orderMore') : t('backToMenu')}</button>
        </div>
      </div>
    </div>
  );
}
